import type { JSX } from 'react';
import type { WireUser, WireUserRole } from '@logistics/contracts';
import { ago, initials, roleLabel } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Switch } from '../shell/switch';
import { sourcesCell } from './source-access';
import { CHANNEL_LABELS, ROLES, telegramCell } from './user-form';

/** The design's six columns plus sources: user, Telegram, email, role, channels, sources, access. */
export const USER_COLUMNS =
  'grid grid-cols-[minmax(0,1.3fr)_minmax(0,1.2fr)_minmax(0,1.2fr)_110px_130px_100px_90px] items-center gap-3.5 px-5';

/** One allowed person (design L403–423). A disabled one is drawn at half opacity. */
export function UserRow({
  user,
  busy,
  onRole,
  onAccess,
  onSources,
  onRemove,
}: {
  user: WireUser;
  busy: boolean;
  onRole: (role: WireUserRole) => void;
  onAccess: (enabled: boolean) => void;
  onSources: () => void;
  onRemove: () => void;
}): JSX.Element {
  const telegram = telegramCell(user);
  return (
    <div
      className={cn(USER_COLUMNS, 'border-line border-b py-3.5 transition-opacity duration-300')}
      style={{ opacity: user.enabled ? 1 : 0.5 }}
    >
      <div className="flex min-w-0 items-center gap-3">
        <span className="bg-surf2 border-line grid size-[34px] flex-none place-items-center rounded-[10px] border text-[12.5px] font-semibold">
          {initials(user.name)}
        </span>
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">{user.name}</div>
          <div className="text-mute text-xs">
            {user.lastSeenAt === undefined ? 'never' : ago(user.lastSeenAt)}
          </div>
        </div>
      </div>
      <div className="truncate font-mono text-[12.5px]">
        {telegram.handle}
        <span className="text-mute"> {telegram.id}</span>
      </div>
      <div className={cn('truncate text-[13px]', user.email === undefined && 'text-mute')}>
        {user.email ?? 'not set'}
      </div>
      <div>
        <RoleSelect user={user} busy={busy} onRole={onRole} />
      </div>
      <div className="flex gap-[5px]">
        {user.channels.map((channel) => (
          <span
            key={channel}
            className="bg-acc-soft text-acc rounded-md px-[7px] py-[3px] font-mono text-[10.5px]"
          >
            {CHANNEL_LABELS[channel]}
          </span>
        ))}
      </div>
      <div>
        <SourcesCell user={user} busy={busy} onSources={onSources} />
      </div>
      <div className="flex items-center justify-end gap-2">
        <Switch
          on={user.enabled}
          disabled={busy}
          label={user.enabled ? `Disable ${user.name}` : `Enable ${user.name}`}
          onChange={onAccess}
        />
        <button
          type="button"
          title="Remove"
          aria-label={`Remove ${user.name}`}
          disabled={busy}
          onClick={onRemove}
          className="border-line text-mute hover:text-bad hover:border-bad size-7 rounded-lg border bg-transparent text-sm leading-none"
        >
          ×
        </button>
      </div>
    </div>
  );
}

/** "All", or how many sources; a button that opens the picker. An admin reads everything. */
function SourcesCell({
  user,
  busy,
  onSources,
}: {
  user: WireUser;
  busy: boolean;
  onSources: () => void;
}): JSX.Element {
  if (user.role === 'admin') return <span className="text-mute text-[12.5px]">All</span>;
  return (
    <button
      type="button"
      disabled={busy}
      aria-label={`Sources of ${user.name}`}
      onClick={onSources}
      className="border-line bg-surf2 rounded-full border px-2 py-[3px] text-xs"
    >
      {sourcesCell(user)}
    </button>
  );
}

/** The role pill, as a small select: an admin changes a role in place. */
function RoleSelect({
  user,
  busy,
  onRole,
}: {
  user: WireUser;
  busy: boolean;
  onRole: (role: WireUserRole) => void;
}): JSX.Element {
  return (
    <select
      aria-label={`Role of ${user.name}`}
      value={user.role}
      disabled={busy}
      onChange={(event) => onRole(event.target.value as WireUserRole)}
      className="border-line bg-surf2 rounded-full border px-2 py-[3px] text-xs"
    >
      {ROLES.map((role) => (
        <option key={role} value={role}>
          {roleLabel(role)}
        </option>
      ))}
    </select>
  );
}
